import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Button,
  Stack,
  Typography,
  IconButton,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";
import SnackState from "../../shared-components/snackbar/snackState";
import { IconX } from "@tabler/icons-react";
import { selectUserDetail } from "../../redux/slice/userSlice";

const { REACT_APP_MAX_IMAGE_FILE_SIZE_MB: Image_Size } = process.env;
const ProfileImageModal = ({
  open,
  onClose,
  onSave,
  initials = "",
  currentProfileImage = null,
}) => {
  const selectedUser = useSelector(selectUserDetail)
  const [profileImage, setProfileImage] = useState(null);
  const [previewProfileImage, setPreviewProfileImage] = useState(null);
  const [snack, closeSnack, showSnackbar] = SnackState();
  const [isImageChanged, setIsImageChanged] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setProfileImage(null);
      setIsImageChanged(false);
      setPreviewProfileImage(currentProfileImage || null);
    }
  }, [open, currentProfileImage]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    e.target.value = "";

    if (!file) return;

    const fileSize = file.size / (1024 * 1024);
    const maxSizeMB = parseFloat(Image_Size);
    if (fileSize > maxSizeMB) {
      showSnackbar(
        `File size exceeds ${Image_Size} MB. Please upload a smaller image.`,
        "error"
      );
      return;
    }

    setIsImageChanged(true);
    setPreviewProfileImage(URL.createObjectURL(file));

    const reader = new FileReader();
    reader.onload = () => {
      setProfileImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemove = () => {
    setPreviewProfileImage(null);
    setProfileImage(null);
    setIsImageChanged(true);
  };

  const handleSave = () => {
    onSave(profileImage);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
        <DialogTitle>
          Profile picture
          <IconButton aria-label="close" onClick={onClose}>
            <IconX />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Avatar
            src={previewProfileImage}
            alt={selectedUser?.firstName}
            sx={{
              width: 200,
              height: 200,
              margin: "0 auto",
              fontSize: 45,
            }}
          >
            {!previewProfileImage && `${selectedUser?.firstName[0]}`}
          </Avatar>
          <Stack direction="row" spacing={2} justifyContent="center" mt={3}>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={handleUploadClick}
            >
              Upload
            </Button>
            <Button
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={handleRemove}
              disabled={!previewProfileImage}
            >
              Remove
            </Button>
          </Stack>

          <input
            type="file"
            accept="image/*"
            hidden
            ref={fileInputRef}
            onChange={handleFileSelect}
          />
        </DialogContent>

        <DialogActions>
          <Button
            size="small"
            onClick={onClose}
            variant="outlined"
            color="primary"
          >
            Close
          </Button>
          <Button
            size="small"
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={!isImageChanged}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <CommonSnackBar
        snackObj={snack}
        closeSnack={closeSnack}
        duration={2000}
      />
    </>
  );
};

export default ProfileImageModal;
